/**
 * Teczka instalacji na koncie prywatnym (2026-10-23): czyste funkcje dla hooks/useTeczka i Projektu 3D.
 *
 * Konto prywatne ma jedną teczkę na koncie (backend `teczka`), także w czasie Premium. Przeglądarka trzyma zapas:
 * projekt pod starym kluczem Projektu 3D (`lib/projekt3d/projekt.js`), pozostałe części pod `vv:teczka` (klucz
 * uzgodniony z sesją „Teczka: miejsca w domu”). Zasady „raz za darmo”: backend `teczka/uslugi.py`.
 */
import { usunZapisany, uzupelnijProjekt, wczytajZapisany, zapiszProjekt } from '../projekt3d/projekt'

export const CZESCI = ['projekt', 'sprzet', 'pieniadze', 'miejsca']
export const KLUCZ_CZESCI = 'vv:teczka'
export const KLUCZ_NIEZAPISANY = 'vv:teczka:projekt-niezapisany'
// Czyja jest praca w przeglądarce: skrót adresu konta, nie sam adres.
export const KLUCZ_KONTA = 'vv:teczka:konto'
// Ta sama zasada co backend (`teczka/uslugi.py`, `ten_sam_budynek`): środek nowego budynku w obrysie budynku z teczki
// (instalacja wolnostojąca: w obszarze) z zapasem, bo domy w szeregu i bliźniaki dzieli od środka do środka ok. 10 m.
// Bez obrysu decyduje odległość środków.
export const ZAPAS_OBRYSU_M = 2
export const ZAPAS_OBSZARU_M = 10
export const TEN_SAM_SRODEK_M = 5

/** Konto z teczką: prywatne, a backend zna teczkę (pole `teczka` w `/me`; starszy backend go nie ma). */
export const kontoZTeczka = (profil) => profil?.account_type === 'private' && profil?.teczka != null

/** Projekt ma budynek (z mapy albo z wymiarów). Pustego projektu, np. po „Nowy projekt”, nie zapisujemy. */
export const projektZbudowany = (p) => Boolean(p && (p.lokalizacja || p.bezMapy))

/** Projekt do teczki bez pory dnia (suwak słońca) i bez powiązania z kartą klienta firmy. */
export const projektDoTeczki = (projekt) => {
  if (!projekt) return null
  const { czas, klient, ...reszta } = projekt
  return reszta
}

export const poczatekSceny = (projekt) => {
  const l = projekt?.lokalizacja
  return l && Number.isFinite(l.e0) && Number.isFinite(l.n0) ? [l.e0, l.n0] : null
}

const punkty = (lista) => (Array.isArray(lista) && lista.length >= 3
  && lista.every((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])) ? lista : null)

/** Wielokąt budynku z teczki w układzie 1992: obrys z mapy albo obszar wolnostojącej (metry sceny, z na południe). */
export const obrysTeczki = (projekt) => {
  const a = poczatekSceny(projekt)
  if (!a) return null
  if (projekt.rodzaj === 'grunt') {
    const obszar = punkty(projekt.grunt?.obszar)
    return obszar ? obszar.map(([x, z]) => [a[0] + x, a[1] - z]) : null
  }
  return punkty(projekt.lokalizacja.obrys)
}

const odlegloscOdOdcinka = ([px, py], [ax, ay], [bx, by]) => {
  const dx = bx - ax
  const dy = by - ay
  const dl2 = dx * dx + dy * dy
  const t = dl2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / dl2))
  return Math.hypot(px - ax - t * dx, py - ay - t * dy)
}

/** Punkt w wielokącie albo najwyżej `zapas` metrów od jego brzegu. */
export const wWielokacie = (p, wielokat, zapas = 0) => {
  let w = false
  const n = wielokat.length
  for (let i = 0; i < n; i += 1) {
    const [x1, y1] = wielokat[i]
    const [x2, y2] = wielokat[(i + 1) % n]
    if ((y1 > p[1]) !== (y2 > p[1]) && p[0] < x1 + ((p[1] - y1) * (x2 - x1)) / (y2 - y1)) w = !w
  }
  return w || wielokat.some((a, i) => odlegloscOdOdcinka(p, a, wielokat[(i + 1) % n]) <= zapas)
}

/**
 * Czy budynek o środku `srodek` ([e, n] w układzie 1992, `null` = bez mapy) i obrysie `obrys` (wielokąt [[e, n]...]
 * albo null) to budynek projektu `wzor`. Środek każdego leży w obrysie drugiego: środek dużego budynku potrafi leżeć
 * tuż przy ścianie małego obok.
 */
export const tenSamBudynek = (wzor, srodek, obrys = null) => {
  if (!projektZbudowany(wzor)) return true
  const a = poczatekSceny(wzor)
  if (!a || !srodek) return !a && !srodek
  if (Math.hypot(a[0] - srodek[0], a[1] - srodek[1]) <= TEN_SAM_SRODEK_M) return true
  const obrysWzoru = obrysTeczki(wzor)
  if (!obrysWzoru) return false
  const zapas = wzor.rodzaj === 'grunt' ? ZAPAS_OBSZARU_M : ZAPAS_OBRYSU_M
  const nowy = punkty(obrys)
  return wWielokacie(srodek, obrysWzoru, zapas) && (!nowy || wWielokacie(a, nowy, zapas))
}

const czytaj = (klucz) => {
  try { return JSON.parse(window.localStorage.getItem(klucz) || 'null') } catch { return null }
}

/** Części teczki z przeglądarki (bez projektu): zapas bez sieci i bez konta. */
export const czesciZPrzegladarki = () => {
  const c = czytaj(KLUCZ_CZESCI)
  return c && typeof c === 'object' && !Array.isArray(c) ? c : {}
}

/** Dopisuje podane części do zapasu w przeglądarce; projekt pomija (ma własny klucz), pozostałe zostawia. */
export const zapiszCzesciWPrzegladarce = (czesci) => {
  const nowe = { ...czesciZPrzegladarki() }
  Object.entries(czesci || {}).forEach(([k, v]) => { if (k !== 'projekt' && CZESCI.includes(k)) nowe[k] = v })
  try { window.localStorage.setItem(KLUCZ_CZESCI, JSON.stringify(nowe)) } catch { /* bez pamięci przeglądarki */ }
}

export const projektNiezapisany = () => {
  try { return window.localStorage.getItem(KLUCZ_NIEZAPISANY) === '1' } catch { return false }
}

export const oznaczProjektNiezapisany = (tak) => {
  try {
    if (tak) window.localStorage.setItem(KLUCZ_NIEZAPISANY, '1')
    else window.localStorage.removeItem(KLUCZ_NIEZAPISANY)
  } catch { /* bez pamięci przeglądarki */ }
}

/** Krótki skrót adresu konta (djb2), żeby w pamięci przeglądarki nie leżał sam adres. */
export const znakKonta = (email) => {
  let h = 5381
  for (const c of String(email || '').trim().toLowerCase()) h = ((h << 5) + h + c.charCodeAt(0)) | 0
  return (h >>> 0).toString(36)
}

/**
 * Wspólny komputer: praca w przeglądarce należy do konta, które ją zostawiło. Gdy loguje się inne konto prywatne,
 * projekt i części z przeglądarki znikają, zanim cokolwiek trafi do jego pustej teczki. Bez znaku (praca sprzed tej
 * zmiany) przejmujemy ją jak dotąd. Zwraca true, gdy wyczyszczono cudzą pracę.
 */
export const przejmijPrzegladarke = (znak) => {
  if (!znak) return false
  let poprzedni = null
  try { poprzedni = window.localStorage.getItem(KLUCZ_KONTA) } catch { return false }
  const obce = Boolean(poprzedni) && poprzedni !== znak
  try {
    if (obce) {
      usunZapisany()
      window.localStorage.removeItem(KLUCZ_CZESCI)
      window.localStorage.removeItem(KLUCZ_NIEZAPISANY)
    }
    window.localStorage.setItem(KLUCZ_KONTA, znak)
  } catch { /* bez pamięci przeglądarki */ }
  return obce
}

/**
 * Przed otwarciem Projektu 3D: projekt z konta trafia do pamięci przeglądarki, skąd czyta go strona. Wyjątek: w
 * przeglądarce czeka praca, która nie doszła na konto (np. bez sieci); wtedy zostaje ona i wyśle się przy otwarciu.
 * Projekt klienta firmy (`klient`, zostawiony tu przez konto firmowe) nie trafi do teczki konta prywatnego.
 * Zwraca true, gdy podstawiono projekt z konta.
 */
export const projektZKontaDoPrzegladarki = (zapisany) => {
  if (!projektZbudowany(zapisany)) {
    if (wczytajZapisany()?.klient) usunZapisany()
    return false
  }
  if (projektNiezapisany() && projektZbudowany(wczytajZapisany())) return false
  zapiszProjekt(uzupelnijProjekt(zapisany))
  return true
}
